import { useContext, useEffect, useRef, useState } from "react";

import { Polyline } from "react-leaflet";
import WrfContext from "../contexts/WrfContext";
import { toApiTimeFormat } from "../shared/tileOverlay";
import { WindSegment, windArrowPositions } from "../shared/windArrow";

// Renders WRF wind-direction arrows over the windspeed raster. Each forecast
// frame has its own `<baseURL>/<time>/wind_direction.json`; we fetch the active
// frame's file (cached per URL) and draw an open arrow per segment. Arrows are
// a fixed style and intentionally ignore the opacity slider (which dims the
// raster) — matching Gmap, where the direction arrows stay solid.
//
// NOTE: wind_direction.json is currently served without CORS headers, so this
// fetch is blocked cross-origin and the arrows render nothing (graceful, warns
// once). It lights up automatically once the `/radar/` static path sends
// `Access-Control-Allow-Origin` — exactly like the tiles did when synced.
const WrfWindArrows = () => {
  const wrf = useContext(WrfContext);
  const [segments, setSegments] = useState<WindSegment[]>([]);
  const cache = useRef<Map<string, WindSegment[]>>(new Map());
  const warned = useRef(false);

  const product = wrf?.product;
  const active = wrf?.activeApiResponse;
  const frame = wrf?.frame ?? 0;
  const time = active?.times[frame];
  const url =
    product === "wind" && active && time
      ? `${active.baseURL}/${toApiTimeFormat(time)}/wind_direction.json`
      : undefined;

  useEffect(() => {
    if (!url) {
      setSegments([]);
      return;
    }
    const cached = cache.current.get(url);
    if (cached) {
      setSegments(cached);
      return;
    }
    // Uncached frame: clear stale arrows first so a slow fetch can't leave the
    // previous frame's arrows over the current windspeed tile during playback.
    setSegments([]);
    let cancelled = false;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: unknown) => {
        // r.json() is `any`; validate the shape so a malformed-but-200 payload
        // can't feed NaN coordinates into Leaflet — route it to the catch.
        if (
          !Array.isArray(data) ||
          !data.every(
            (s) =>
              Array.isArray(s) &&
              s.length === 4 &&
              // Number.isFinite rejects non-numbers, NaN and ±Infinity (e.g. a
              // JSON 1e999 that r.json() turns into Infinity).
              s.every((n) => Number.isFinite(n))
          )
        ) {
          throw new Error("malformed wind_direction.json");
        }
        const segs = data as WindSegment[];
        cache.current.set(url, segs);
        if (!cancelled) setSegments(segs);
      })
      .catch((e: any) => {
        if (!cancelled) setSegments([]);
        if (!warned.current) {
          warned.current = true;
          console.warn(
            "WRF wind-direction arrows unavailable:",
            e?.message ?? e
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (product !== "wind" || !active) return null;

  return (
    <>
      {segments.map((seg, i) => (
        <Polyline
          // Index key: updates positions in place as frames change, and is
          // collision-proof (segment start points come from untrusted remote
          // data and could otherwise alias).
          key={i}
          positions={windArrowPositions(seg)}
          pathOptions={{ color: "#000", weight: 1, opacity: 0.85 }}
          interactive={false}
        />
      ))}
    </>
  );
};

export default WrfWindArrows;
